import {
  createFileRoute,
  isRedirect,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  AnimatePresence,
  MotionConfig,
  motion,
  type Variants,
} from "framer-motion";
import { useEffect, useRef } from "react";
import { RecipeFab } from "@/components/recipes/RecipeFab";
import { StableOutlet } from "@/components/StableOutlet";
import { clearPendingInvite, getPendingInvite } from "@/lib/account-storage";
import { authClient } from "@/lib/auth-client";
import { useSync, useUpdatePrompt } from "@/lib/sync/use-sync";

let cachedSession: unknown = null;

export const Route = createFileRoute("/_app")({
  beforeLoad: async () => {
    // If we already have a cached session, skip the network call.
    // useSession() in AppLayout handles revalidation reactively.
    if (cachedSession) return;

    try {
      const session = await authClient.getSession();
      if (session.data?.user.emailVerified) {
        cachedSession = session.data;
      } else {
        // No session, or a session whose email isn't verified yet — the app
        // requires a verified email (see requireEmailVerification server-side).
        throw redirect({ to: "/auth/$path", params: { path: "sign-in" } });
      }
    } catch (e) {
      if (isRedirect(e)) throw e;
      // Network error and no cached session — redirect to sign-in
      // since we can't verify auth state at all.
      if (!cachedSession) {
        throw redirect({ to: "/auth/$path", params: { path: "sign-in" } });
      }
      // Network error but have cached session — allow through.
      // The app is local-first; data is in IndexedDB.
    }
  },
  component: AppLayout,
});

const NEW_RECIPE_ROUTE_ID = "/_app/_recipe-page/recipes/new";

type TransitionKey =
  | "shell"
  | "recipe-new"
  | "recipe-detail-or-edit"
  | "meal-detail";

function outerTransitionKey(routeId: string): TransitionKey {
  if (routeId.startsWith("/_app/_shell")) return "shell";
  if (routeId === NEW_RECIPE_ROUTE_ID) return "recipe-new";
  if (routeId.startsWith("/_app/_meal-page")) return "meal-detail";
  return "recipe-detail-or-edit";
}

// Recipe pages render above the shell during the brief moments they overlap it
// — e.g. New's fade in/out as the shell reappears underneath. Shell itself must
// NOT get an explicit z-index: doing so creates a CSS stacking context that traps
// its children's own z-index (like BottomNav's) inside it, so anything
// portaled elsewhere in the document (e.g. the shopping page's always-open
// AddItemDrawer, rendered via a portal sibling to the app root) would then
// paint over it regardless of BottomNav's z-10.
const zIndexByKey: Record<TransitionKey, string> = {
  shell: "",
  "recipe-new": "z-10",
  "recipe-detail-or-edit": "z-10",
  "meal-detail": "z-10",
};

const noopVariants: Variants = { initial: {}, animate: {}, exit: {} };
const fadeVariants: Variants = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  exit: { opacity: 0 },
};
// Exit is a no-op here: Detail's back/delete actions animate the page's own
// (still-live, correctly-rendered) content out imperatively *before*
// navigating away — see recipes.$recipeId.tsx — rather than relying on
// this wrapper's AnimatePresence exit, since <Outlet/> would otherwise
// swap to show the list mid-slide (see StableOutlet above).
const slideVariants: Variants = {
  initial: { x: "100%" },
  animate: { x: 0 },
  exit: {},
};

function AppLayout() {
  const { data: session } = authClient.useSession();
  const navigate = useNavigate();
  useSync(session?.user.accountId);
  useUpdatePrompt();

  // An invitee who opened an invite link while signed out is bounced through
  // sign-in/up and lands here; return them to the accept page to finish.
  useEffect(() => {
    if (!session) return;
    const pending = getPendingInvite();
    if (pending) {
      clearPendingInvite();
      navigate({ to: "/invite/$token", params: { token: pending } });
    }
  }, [session, navigate]);

  // Clear the route guard cache when session is lost (sign-out / expiry)
  // so the beforeLoad guard redirects unauthenticated users correctly.
  if (!session) {
    cachedSession = null;
  }

  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const leafRouteId = useRouterState({
    select: (s) => s.matches.at(-1)?.routeId ?? "",
  });
  const transitionKey = outerTransitionKey(leafRouteId);
  const showFab = pathname === "/recipes";

  // Track the key we're transitioning *from* so Detail arriving straight
  // from a just-created recipe can continue New's crossfade instead of
  // replaying the generic slide-in (Detail<->Edit crossfades the same way,
  // via _recipe-page.tsx's own inner AnimatePresence, since the outer key
  // doesn't change between them).
  const prevKeyRef = useRef<TransitionKey>(transitionKey);
  const fromKey = prevKeyRef.current;
  useEffect(() => {
    prevKeyRef.current = transitionKey;
  }, [transitionKey]);
  const continuesFromNew =
    transitionKey === "recipe-detail-or-edit" && fromKey === "recipe-new";
  // Returning from a recipe pushed on top of a meal day (via its "view
  // recipe" button) should reveal the day underneath, not replay its
  // forward slide-in — mirrors continuesFromNew's direction-dependent
  // variant choice above.
  const returningFromRecipeDetail =
    transitionKey === "meal-detail" && fromKey === "recipe-detail-or-edit";

  const variants =
    transitionKey === "shell"
      ? noopVariants
      : transitionKey === "recipe-new" || continuesFromNew
        ? fadeVariants
        : returningFromRecipeDetail
          ? noopVariants
          : slideVariants;

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative h-svh overflow-hidden">
        <AnimatePresence initial={false}>
          <motion.div
            key={transitionKey}
            className={`absolute inset-0 bg-background ${zIndexByKey[transitionKey]}`}
            variants={variants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.28, ease: "easeInOut" }}
          >
            <StableOutlet>
              <Outlet />
            </StableOutlet>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>{showFab && <RecipeFab />}</AnimatePresence>
      </div>
    </MotionConfig>
  );
}
